import React from 'react';
import { Icon } from '../Icon/Icon';
import { EmptyAnimatedIllustration } from './EmptyAnimatedIllustration';
import './receptionist-empty.css';

function FeatureStrip() {
  const pillars = [
    { icon: 'stopwatch' as const, title: 'Always on', sub: '24/7 · weekends · after hours', stat: '24/7' },
    { icon: 'shield-check' as const, title: 'HIPAA-ready', sub: 'Encrypted at rest & in transit', stat: 'AES-256' },
    { icon: 'headphones' as const, title: 'Instant handoffs', sub: 'Routed to your team in seconds', stat: '< 45s' },
  ];

  return (
    <div className="rcpt-empty-fade-up overflow-hidden rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-sm [animation-delay:60ms]">
      <div className="grid grid-cols-3">
        {pillars.map((pillar, i) => (
          <div
            key={pillar.title}
            className={[
              'flex items-center gap-3.5 px-[22px] py-5',
              i < pillars.length - 1 ? 'border-r border-[var(--cream-2)]' : '',
            ].join(' ')}
          >
            <div className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border border-[color-mix(in_srgb,var(--ai-3)_15%,transparent)] bg-[var(--ai-tint)] text-[var(--ai-3)]">
              <Icon kind={pillar.icon} size={17} stroke={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-[var(--font-display)] text-[14.5px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink-0)]">
                {pillar.title}
              </div>
              <div className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">{pillar.sub}</div>
            </div>
            <span className="rounded border border-[var(--cream-2)] bg-[var(--surface-mute)] px-[7px] py-[3px] font-[var(--font-mono)] text-[11px] font-semibold text-[var(--ink-2)]">
              {pillar.stat}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmptyDashboard() {
  return (
    <>
      <div className="rcpt-empty-fade-up relative mb-[18px] flex min-h-[540px] items-center gap-7 overflow-hidden rounded-[20px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-11 pb-9 pt-10">
        <div className="rcpt-empty-aurora-1" />
        <div className="rcpt-empty-aurora-2" />
        <div className="rcpt-empty-aurora-3" />
        <div className="rcpt-empty-rim" />

        <div className="relative z-[1] min-w-0 max-w-[480px] flex-1">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[color-mix(in_srgb,var(--ai-3)_15%,transparent)] bg-[var(--ai-tint)] py-[5px] pl-2.5 pr-3">
            <span className="elva-live" />
            <span className="font-[var(--font-mono)] text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--ai-3)]">
              Live now · listening
            </span>
          </div>

          <h1 className="m-0 mb-4 font-[var(--font-display)] text-[56px] font-extrabold leading-[0.98] tracking-[-0.045em] text-[var(--ink-0)]">
            <span className="rcpt-empty-iri-text">Elva</span> is at your
            <br />
            front desk.
          </h1>

          <p className="m-0 mb-7 max-w-[44ch] text-[16px] font-normal leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
            She&apos;ll start answering the moment your number rings. Watch every call in real time and
            review every outcome here.
          </p>

          <div className="mb-7 flex items-center gap-2.5">
            <a
              href="/live-feed"
              className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-[10px] border border-[var(--ai-3)] bg-[var(--ai-3)] px-5 text-[14px] font-semibold text-white no-underline shadow-[0_0_0_1px_var(--ai-tint),0_6px_18px_var(--ai-tint)]"
            >
              <Icon kind="activity" size={14} stroke={2} />
              Open Live Feed
            </a>
            <button
              type="button"
              className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-[10px] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 text-[14px] font-medium text-[var(--ink-0)] transition-colors hover:bg-[var(--cream-1)]"
            >
              <Icon kind="phone" size={14} stroke={2} />
              Place a test call
            </button>
          </div>

          <div className="inline-flex items-center gap-3 rounded-[11px] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-3.5 py-2.5">
            <div className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Forwarding to
            </div>
            <div className="font-[var(--font-mono)] text-[14px] font-semibold tracking-[0.02em] text-[var(--ink-0)]">
              +1 (415) 555-0100
            </div>
            <span className="h-3.5 w-px bg-[var(--cream-2)]" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--ai-tint)] px-2 py-[3px] text-[10.5px] font-semibold text-[var(--ai-3)]">
              <Icon kind="check" size={9} stroke={2.5} />
              Active
            </span>
          </div>
        </div>

        <div className="relative z-[1] shrink-0">
          <EmptyAnimatedIllustration />
        </div>
      </div>

      <FeatureStrip />
    </>
  );
}
